import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/signups')({
  component: AdminSignupsRoute,
})

function AdminSignupsRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.adminSignups>>['items']>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data = await api.adminSignups(token, {
          limit: 200,
          ...(filters.source ? { source: filters.source } : {}),
          ...(filters.country ? { country: filters.country } : {}),
        })
        setRows(data.items)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load signups.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [filters.country, filters.source, token])

  const filteredRows = useMemo(() => {
    if (!filters.query.trim()) {
      return rows
    }

    const query = filters.query.trim().toLowerCase()
    return rows.filter(
      (row) => row.userId.toLowerCase().includes(query) || row.email.toLowerCase().includes(query)
    )
  }, [filters.query, rows])

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Signups Feed</h1>
        <p className={styles.subtitle}>
          Inspect signup source attribution, geo, and promoter linkage.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            Source
            <input
              className={styles.input}
              value={filters.source}
              onChange={(event) =>
                setFilters((current) => ({ ...current, source: event.target.value }))
              }
              placeholder="promoter, direct, organic"
            />
          </label>
          <label>
            Country
            <input
              className={styles.input}
              value={filters.country}
              onChange={(event) =>
                setFilters((current) => ({ ...current, country: event.target.value }))
              }
              placeholder="US"
            />
          </label>
          <label>
            Search
            <input
              className={styles.input}
              value={filters.query}
              onChange={(event) =>
                setFilters((current) => ({ ...current, query: event.target.value }))
              }
              placeholder="user id or email"
            />
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
          <button
            className={styles.ghostButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                ...filters,
                source: '',
                country: '',
                query: '',
              })
            }
          >
            Reset Signup Filters
          </button>
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading signups...</p>
        </section>
      )}

      {token && error && (
        <section className={styles.card}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Retry
          </button>
        </section>
      )}

      {token && !isLoading && !error && (
        <section className={styles.card}>
          <h2>Rows</h2>
          {filteredRows.length === 0 ? (
            <p className={styles.emptyState}>No signups match this filter.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Geo</th>
                  <th>Promoter</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const search = window.location.search
                  const userHref = `/admin/users/${encodeURIComponent(row.userId)}${search}`
                  return (
                    <tr key={row.id}>
                      <td>
                        <a className={styles.tabLink} href={userHref}>
                          {row.userId}
                        </a>
                      </td>
                      <td>{row.email}</td>
                      <td>{row.firstTouchSource ?? '-'}</td>
                      <td>
                        {row.countryCode ?? '-'} {row.regionCode ?? ''}
                      </td>
                      <td>{row.promoterId ?? '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  )
}
